// src/services/mockApiService.js
// Lightweight in-memory + sessionStorage backed mock API that simulates backend endpoints.
// It is invoked by apiService when NO_SERVER mode is enabled.

import { mapPreferenceToGroupApiFormat, mapPreferenceToTeacherApiFormat, mapPreferenceFromApiFormat } from '../utils/preferenceMappers';
import { isDemoBuild } from '../config/AppStatic.js';
import { getWeekNumber } from '../utils/helpers.ts';
import logger from '../utils/logger';
import { cleanInheritedTitle } from '../utils/scheduleDraftKind';

const STORAGE_KEY = 'piSchedule.mockData';
const ID_TRACKER_KEY = 'piSchedule.mockData.idTracker';
const DEMO_WEEK_ANCHOR_KEY = 'piSchedule.demoWeekAnchor';

/** Fall timetable weeks in mock-data.json (inclusive); mapped relative to today's ISO week in demo builds. */
const DEMO_MOCK_FALL_SOURCE_WEEK_MIN = 39;
const DEMO_MOCK_FALL_SOURCE_WEEK_MAX = 52;
/** Spring timetable weeks in mock-data.json (inclusive); mapped the same way for Spring-period entries. */
const DEMO_MOCK_SPRING_SOURCE_WEEK_MIN = 1;
const DEMO_MOCK_SPRING_SOURCE_WEEK_MAX = 22;
/** Full academic-week span used when shifting period boundaries (includes Spring 1–22). */
const DEMO_PERIOD_SOURCE_WEEK_MIN = 1;
const DEMO_PERIOD_SOURCE_WEEK_MAX = 52;

let inMemoryStore = null;
let idTracker = null;

const getDemoWeekAnchor = () => {
    const now = new Date();
    return `${now.getFullYear()}-${getWeekNumber(now)}`;
};

const clampWeek = (w) => Math.min(53, Math.max(1, Math.round(w)));

const inferDemoAcademicStartYear = (now, yearStructure) => {
    const startWeek = Number(yearStructure?.startWeek);
    const endWeek = Number(yearStructure?.endWeek);
    const w = getWeekNumber(now);
    const y = now.getFullYear();
    if (!Number.isFinite(startWeek) || !Number.isFinite(endWeek) || startWeek <= endWeek) {
        return y;
    }
    if (w >= startWeek) return y;
    if (w <= endWeek) return y - 1;
    return y;
};

const demoMapWeekSpanToAnchorWindow = (sourceWeek, sourceMin, sourceMax, todayIsoWeek) => {
    if (sourceWeek < sourceMin || sourceWeek > sourceMax) {
        return clampWeek(sourceWeek);
    }
    const spanSrc = sourceMax - sourceMin;
    const dstMin = todayIsoWeek - 3;
    const dstMax = todayIsoWeek + 9;
    const spanDst = dstMax - dstMin;
    const mapped = dstMin + ((sourceWeek - sourceMin) * spanDst) / spanSrc;
    return clampWeek(mapped);
};

const demoMapFallSourceWeekToAnchorWeek = (sourceWeek, todayIsoWeek) => (
    demoMapWeekSpanToAnchorWindow(sourceWeek, DEMO_MOCK_FALL_SOURCE_WEEK_MIN, DEMO_MOCK_FALL_SOURCE_WEEK_MAX, todayIsoWeek)
);

const demoMapSpringSourceWeekToAnchorWeek = (sourceWeek, todayIsoWeek) => (
    demoMapWeekSpanToAnchorWindow(sourceWeek, DEMO_MOCK_SPRING_SOURCE_WEEK_MIN, DEMO_MOCK_SPRING_SOURCE_WEEK_MAX, todayIsoWeek)
);

const demoMapEntrySourceWeekToAnchorWeek = (sourceWeek, todayIsoWeek, periodId) => {
    if (String(periodId) === '2') {
        return demoMapSpringSourceWeekToAnchorWeek(sourceWeek, todayIsoWeek);
    }
    return demoMapFallSourceWeekToAnchorWeek(sourceWeek, todayIsoWeek);
};

const demoMapPeriodWeekToAnchorWeek = (sourceWeek, todayIsoWeek) => (
    demoMapWeekSpanToAnchorWindow(sourceWeek, DEMO_PERIOD_SOURCE_WEEK_MIN, DEMO_PERIOD_SOURCE_WEEK_MAX, todayIsoWeek)
);

const expandWeekSeriesToWeeks = (weekSeriesStr) => {
    const weeks = new Set();
    if (!weekSeriesStr || typeof weekSeriesStr !== 'string') return [];
    for (const raw of weekSeriesStr.split(',').map((s) => s.trim()).filter(Boolean)) {
        if (raw.includes('-')) {
            const [as, bs] = raw.split('-').map((s) => parseInt(s.trim(), 10));
            if (isNaN(as) || isNaN(bs)) continue;
            if (as <= bs) {
                for (let w = as; w <= bs; w++) weeks.add(w);
            } else {
                for (let w = as; w <= 53; w++) weeks.add(w);
                for (let w = 1; w <= bs; w++) weeks.add(w);
            }
        } else {
            const w = parseInt(raw, 10);
            if (!isNaN(w)) weeks.add(w);
        }
    }
    return [...weeks].sort((a, b) => a - b);
};

const uniqSortedWeeks = (weeks) => [...new Set(weeks)].sort((a, b) => a - b);

const weeksToCompactSeries = (sortedUnique) => {
    if (!sortedUnique.length) return '';
    const parts = [];
    let rangeStart = sortedUnique[0];
    let prev = sortedUnique[0];
    for (let i = 1; i < sortedUnique.length; i++) {
        const w = sortedUnique[i];
        if (w === prev + 1) {
            prev = w;
            continue;
        }
        parts.push(rangeStart === prev ? String(rangeStart) : `${rangeStart}-${prev}`);
        rangeStart = w;
        prev = w;
    }
    parts.push(rangeStart === prev ? String(rangeStart) : `${rangeStart}-${prev}`);
    return parts.join(',');
};

const remapWeekSeriesForDemo = (weekSeriesStr, todayIsoWeek, periodId) => {
    const expanded = expandWeekSeriesToWeeks(weekSeriesStr);
    const mapped = expanded.map((w) => demoMapEntrySourceWeekToAnchorWeek(w, todayIsoWeek, periodId));
    return weeksToCompactSeries(uniqSortedWeeks(mapped));
};

const shiftDemoScheduleEntryWeeksInPlace = (entry, todayIsoWeek, academicStartYear) => {
    if (!entry || typeof entry !== 'object') return;
    const periodId = entry.period_id ?? entry.periodId;
    if (entry.week_series) {
        entry.week_series = remapWeekSeriesForDemo(entry.week_series, todayIsoWeek, periodId);
    }
    if (entry.weekSeries) {
        entry.weekSeries = remapWeekSeriesForDemo(entry.weekSeries, todayIsoWeek, periodId);
    }
    if (entry.year != null) entry.year = academicStartYear;
};

const shiftDemoPeriodWeekField = (period, fieldCamel, fieldSnake, todayIsoWeek) => {
    const raw = period[fieldCamel] ?? period[fieldSnake];
    if (raw == null || Number.isNaN(Number(raw))) return;
    const mapped = demoMapPeriodWeekToAnchorWeek(Number(raw), todayIsoWeek);
    if (fieldCamel in period) period[fieldCamel] = mapped;
    if (fieldSnake in period) period[fieldSnake] = mapped;
};

const applyDemoPeriodCalendarYears = (period, calendarYear) => {
    const startWeek = period.startWeek ?? period.start_week;
    const endWeek = period.endWeek ?? period.end_week;
    if (startWeek == null || endWeek == null) return;

    const y = calendarYear;
    const endYear = endWeek < startWeek ? y + 1 : y;
    if (period.startWeekYear != null) period.startWeekYear = y;
    if (period.start_week_year != null) period.start_week_year = y;
    if (period.endWeekYear != null) period.endWeekYear = endYear;
    if (period.end_week_year != null) period.end_week_year = endYear;
    if (period.year != null) period.year = y;

    delete period.startDate;
    delete period.start_date;
    delete period.endDate;
    delete period.end_date;
};

const shiftDemoPeriodWeeksInPlace = (period, todayIsoWeek, calendarYear) => {
    if (!period || typeof period !== 'object') return;
    shiftDemoPeriodWeekField(period, 'startWeek', 'start_week', todayIsoWeek);
    shiftDemoPeriodWeekField(period, 'endWeek', 'end_week', todayIsoWeek);
    applyDemoPeriodCalendarYears(period, calendarYear);
};

const periodWeekSpanIncludesWeek = (startWeek, endWeek, targetWeek) => {
    if (startWeek == null || endWeek == null || targetWeek == null) return false;
    if (endWeek >= startWeek) {
        return targetWeek >= startWeek && targetWeek <= endWeek;
    }
    return targetWeek >= startWeek || targetWeek <= endWeek;
};

/** Tighten period bounds to shifted entry weeks when entries sit in the demo anchor window. */
const syncDemoPeriodBoundsFromShiftedEntries = (store, todayIsoWeek, calendarYear) => {
    const dstMin = todayIsoWeek - 3;
    const dstMax = todayIsoWeek + 9;

    for (const period of store.yearStructure?.periods || []) {
        const periodId = period.id;
        const entries = (store.schedule_entries || []).filter((e) => (
            String(e.period_id ?? e.periodId) === String(periodId)
            && e.discard !== true
        ));
        if (!entries.length) continue;

        const weeks = [];
        entries.forEach((e) => {
            expandWeekSeriesToWeeks(e.week_series || e.weekSeries || '').forEach((w) => weeks.push(w));
        });
        if (!weeks.length) continue;

        const sorted = uniqSortedWeeks(weeks);
        const minWeek = sorted[0];
        const maxWeek = sorted[sorted.length - 1];
        const hasWeeksInDemoAnchorWindow = sorted.some((w) => w >= dstMin && w <= dstMax);
        if (!hasWeeksInDemoAnchorWindow) {
            continue;
        }

        period.startWeek = minWeek;
        period.start_week = minWeek;
        period.endWeek = maxWeek;
        period.end_week = maxWeek;

        const entryYear = entries.find((e) => e.year != null)?.year ?? calendarYear;
        applyDemoPeriodCalendarYears(period, entryYear);
    }
};

/** Expand academic-year timeline bounds so PeriodTimelineChart includes today and shifted periods. */
const shiftDemoYearStructureBounds = (store, todayIsoWeek, calendarYear) => {
    const ys = store.yearStructure;
    if (!ys) return;

    let minW = todayIsoWeek;
    let maxW = todayIsoWeek;

    for (const period of ys.periods || []) {
        const sw = Number(period.startWeek ?? period.start_week);
        const ew = Number(period.endWeek ?? period.end_week);
        if (!Number.isFinite(sw) || !Number.isFinite(ew)) continue;
        if (ew >= sw) {
            minW = Math.min(minW, sw);
            maxW = Math.max(maxW, ew);
        } else {
            minW = Math.min(minW, 1, ew);
            maxW = Math.max(maxW, sw, 53);
        }
    }

    minW = clampWeek(minW - 2);
    maxW = clampWeek(maxW + 2);

    ys.startWeek = minW;
    ys.endWeek = maxW;
    ys.academicYear = `${calendarYear}-${calendarYear + 1}`;
};

/**
 * Slide mock-data weeks 39–52 onto ISO weeks [today−3 … today+9] so demos always cover "around now".
 * Periods use the full 1–52 source span; year-structure bounds follow shifted periods + today.
 */
const applyDemoScheduleWeekAnchorShift = (store) => {
    if (!store || !isDemoBuild()) return;
    const now = new Date();
    const todayIsoWeek = clampWeek(getWeekNumber(now));
    const calendarYear = now.getFullYear();
    const academicStartYear = inferDemoAcademicStartYear(now, store.yearStructure);

    for (const e of store.schedule_entries || []) {
        shiftDemoScheduleEntryWeeksInPlace(e, todayIsoWeek, academicStartYear);
    }
    for (const d of store.drafts || []) {
        for (const e of d.entries || []) {
            shiftDemoScheduleEntryWeeksInPlace(e, todayIsoWeek, academicStartYear);
        }
    }

    for (const p of store.yearStructure?.periods || []) {
        shiftDemoPeriodWeeksInPlace(p, todayIsoWeek, calendarYear);
    }
    syncDemoPeriodBoundsFromShiftedEntries(store, todayIsoWeek, calendarYear);
    shiftDemoYearStructureBounds(store, todayIsoWeek, calendarYear);
};

const resolveScheduleDraftById = (store, draftId) => {
    if (draftId == null || draftId === '') return null;
    const drafts = store.schedule_drafts || store.drafts || [];
    return drafts.find((d) => String(d.id) === String(draftId)) || null;
};

const filterEntriesForActivePeriod = (store, activePeriodId) => {
    const period = (store.yearStructure?.periods || []).find((p) => String(p.id) === String(activePeriodId));
    if (!period) return [];

    const activeDraftId = period.active_schedule_draft_id ?? period.activeScheduleDraftId;
    const activeDraft = activeDraftId ? resolveScheduleDraftById(store, activeDraftId) : null;
    const allEntries = store.schedule_entries || [];

    if (activeDraftId) {
        const embedded = activeDraft?.entries;
        if (Array.isArray(embedded) && embedded.length > 0) {
            return embedded;
        }
        return allEntries.filter((e) => (
            String(e.schedule_draft_id ?? e.scheduleDraftId) === String(activeDraftId)
            && (e.discard === false || e.discard == null)
        ));
    }

    return allEntries.filter((e) => (
        String(e.period_id ?? e.periodId) === String(activePeriodId)
        && (e.draft_name == null || e.draft_name === '')
        && (e.discard === false || e.discard == null)
    ));
};

const ensureScheduleDrafts = (store) => {
    if (!Array.isArray(store.schedule_drafts)) {
        store.schedule_drafts = [];
    }
    return store.schedule_drafts;
};

const findScheduleDraftByName = (store, draftName) => {
    const normalized = decodeURIComponent(String(draftName || ''));
    const fromMetadata = ensureScheduleDrafts(store).find(
        (d) => d.draft_name === normalized || d.name === normalized
    );
    if (fromMetadata) return fromMetadata;
    return (store.drafts || []).find((d) => d.draft_name === normalized || d.name === normalized) || null;
};

const nextVersionNumberInStore = (store, periodId) => {
    const drafts = ensureScheduleDrafts(store);
    let max = 0;
    drafts.forEach((draft) => {
        const pid = draft.period_id ?? draft.periodId ?? null;
        const samePeriod = periodId == null || periodId === ''
            ? pid == null || pid === ''
            : String(pid) === String(periodId);
        if (!samePeriod) return;
        const n = Number(draft.version_number);
        if (Number.isFinite(n) && n > max) max = n;
    });
    return max + 1;
};

const archiveOutgoingLiveDraftInStore = (store, period, nextDraftId) => {
    const outgoingId = period?.active_schedule_draft_id ?? period?.activeScheduleDraftId;
    if (outgoingId == null || outgoingId === '') return;

    const outgoing = resolveScheduleDraftById(store, outgoingId);
    if (!outgoing) return;

    const nextId = nextDraftId == null || nextDraftId === '' ? null : Number(nextDraftId);
    const isHistory = outgoing.kind === 'history'
        || String(outgoing.draft_name || outgoing.name || '').startsWith('__history__-');
    if (isHistory) {
        outgoing.replaced_by_draft_id = nextId;
        return;
    }

    const previousName = outgoing.draft_name || outgoing.name;
    const historyName = `__history__-${period.id}-${new Date().toISOString().replace(/[:.]/g, '-')}-${Math.random().toString(36).slice(2, 6)}`;
    (store.schedule_entries || []).forEach((entry) => {
        const entryDraftId = entry.schedule_draft_id ?? entry.scheduleDraftId;
        if (String(entryDraftId) === String(outgoing.id) || entry.draft_name === previousName) {
            entry.draft_name = historyName;
            entry.period_id = period.id;
            entry.periodId = period.id;
        }
    });
    if (!outgoing.display_name) {
        outgoing.display_name = cleanInheritedTitle(previousName) || previousName;
    }
    outgoing.draft_name = historyName;
    outgoing.name = historyName;
    outgoing.kind = 'history';
    outgoing.replaced_by_draft_id = nextId;
    outgoing.period_id = period.id;
    outgoing.created_at = new Date().toISOString();
};

const periodSnippetFromStore = (store, periodId) => {
    if (periodId == null || periodId === '') return null;
    const period = (store.yearStructure?.periods || []).find((p) => String(p.id) === String(periodId));
    if (!period) return null;
    return {
        id: period.id,
        name: period.name,
        start_week: period.startWeek ?? period.start_week,
        end_week: period.endWeek ?? period.end_week,
        year: period.year ?? period.startWeekYear ?? period.start_week_year ?? null
    };
};

const countEntriesForScheduleDraft = (store, draftId, draftName) => {
    return (store.schedule_entries || []).filter((e) => {
        if (e.discard === true) return false;
        const entryDraftId = e.schedule_draft_id ?? e.scheduleDraftId;
        if (draftId != null && String(entryDraftId) === String(draftId)) return true;
        if (draftName && e.draft_name === draftName) return true;
        return false;
    }).length;
};

const buildDraftListFromStore = (store) => {
    const metadataRows = ensureScheduleDrafts(store);
    const drafts = metadataRows.map((meta) => {
        const draftId = meta.id;
        const draftName = meta.draft_name ?? meta.name;
        const periodId = meta.period_id ?? meta.periodId ?? null;
        const replacedBy = meta.replaced_by_draft_id
            ? metadataRows.find((row) => Number(row.id) === Number(meta.replaced_by_draft_id))
            : null;
        return {
            id: draftId,
            draft_name: draftName,
            display_name: meta.display_name ?? null,
            version_number: meta.version_number ?? null,
            year: meta.year ?? null,
            last_modified: meta.updated_at ?? meta.last_modified ?? null,
            created_at: meta.created_at ?? meta.updated_at ?? null,
            entries_count: countEntriesForScheduleDraft(store, draftId, draftName),
            period_id: periodId,
            period: periodSnippetFromStore(store, periodId),
            kind: meta.kind || 'version',
            source_draft_id: meta.source_draft_id ?? null,
            source_draft_name: null,
            replaced_by_draft_id: meta.replaced_by_draft_id ?? null,
            replaced_by_draft_name: replacedBy?.draft_name ?? null,
            replaced_by_display_name: replacedBy?.display_name ?? null
        };
    });

    for (const legacy of store.drafts || []) {
        const draftName = legacy.draft_name ?? legacy.name;
        if (!draftName) continue;
        if (drafts.some((d) => d.draft_name === draftName)) continue;
        const embeddedCount = Array.isArray(legacy.entries) ? legacy.entries.length : 0;
        drafts.push({
            id: legacy.id ?? null,
            draft_name: draftName,
            display_name: legacy.display_name ?? null,
            version_number: legacy.version_number ?? null,
            year: legacy.year ?? null,
            last_modified: legacy.last_modified ?? null,
            created_at: legacy.created_at ?? legacy.last_modified ?? null,
            entries_count: embeddedCount || countEntriesForScheduleDraft(store, legacy.id, draftName),
            period_id: legacy.period_id ?? legacy.periodId ?? null,
            period: periodSnippetFromStore(store, legacy.period_id ?? legacy.periodId),
            kind: legacy.kind || 'version',
            source_draft_id: legacy.source_draft_id ?? null,
            source_draft_name: legacy.source_draft_name ?? null,
            replaced_by_draft_id: legacy.replaced_by_draft_id ?? null,
            replaced_by_draft_name: legacy.replaced_by_draft_name ?? null,
            replaced_by_display_name: legacy.replaced_by_display_name ?? null
        });
    }

    return drafts.sort((a, b) => {
        const aTime = a.last_modified ? new Date(a.last_modified).getTime() : 0;
        const bTime = b.last_modified ? new Date(b.last_modified).getTime() : 0;
        return bTime - aTime;
    });
};

const entryMatchesDraftName = (store, entry, draftName) => {
    if (!draftName || draftName === 'live') {
        return entry.draft_name == null || entry.draft_name === '';
    }
    const meta = findScheduleDraftByName(store, draftName);
    if (meta?.id != null) {
        const entryDraftId = entry.schedule_draft_id ?? entry.scheduleDraftId;
        if (String(entryDraftId) === String(meta.id)) return true;
    }
    return entry.draft_name === draftName;
};

const ensureInitialized = async () => {
    if (inMemoryStore) return inMemoryStore;

    const demo = isDemoBuild();
    const anchorNow = demo ? getDemoWeekAnchor() : null;

    try {
        const cached = sessionStorage.getItem(STORAGE_KEY);
        const tracker = sessionStorage.getItem(ID_TRACKER_KEY);
        const anchorStored = sessionStorage.getItem(DEMO_WEEK_ANCHOR_KEY);
        const cacheValidForDemoWeek = !(demo && anchorStored !== anchorNow);
        if (cached && cacheValidForDemoWeek) {
            inMemoryStore = JSON.parse(cached);
            idTracker = tracker ? JSON.parse(tracker) : {};
            return inMemoryStore;
        }
    } catch (e) {
        // ignore
    }

    const resp = await fetch('/mock-data.json');
    if (!resp.ok) {
        throw new Error(`Failed to load mock-data.json: ${resp.status}`);
    }
    inMemoryStore = await resp.json();
    applyDemoScheduleWeekAnchorShift(inMemoryStore);
    // Initialize tracker with max existing ids per collection
    idTracker = {};
    const collections = ['teachers', 'groups', 'students', 'rooms', 'subjects', 'schedule_entries', 'schedule_drafts', 'invitations', 'admins', 'drafts'];
    collections.forEach(col => {
        const arr = Array.isArray(inMemoryStore[col]) ? inMemoryStore[col] : [];
        idTracker[col] = arr.reduce((max, item) => item && item.id && item.id > max ? item.id : max, 0);
    });
    try {
        if (demo && anchorNow) {
            sessionStorage.setItem(DEMO_WEEK_ANCHOR_KEY, anchorNow);
        }
    } catch (e) {
        // ignore
    }
    persist();
    return inMemoryStore;
};

const persist = () => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(inMemoryStore));
        sessionStorage.setItem(ID_TRACKER_KEY, JSON.stringify(idTracker));
    } catch (e) {
        // ignore
    }
};

const nextId = (collectionName) => {
    if (!idTracker[collectionName]) idTracker[collectionName] = 0;
    idTracker[collectionName] += 1;
    persist();
    return idTracker[collectionName];
};

const jsonResponse = (data) => data;

const parsePath = (path) => {
    const clean = path.startsWith('/') ? path.slice(1) : path;
    const parts = clean.split('?')[0].split('/');
    return parts;
};

// Helpers
const getById = (arr, id) => arr.find(i => String(i.id) === String(id));
const removeById = (arr, id) => {
    const idx = arr.findIndex(i => String(i.id) === String(id));
    if (idx >= 0) arr.splice(idx, 1);
};

// Helper to resolve qualifiedSubjects from IDs to full objects
const resolveQualifiedSubjects = (subjectIds) => {
    if (!Array.isArray(subjectIds)) return [];
    const subjects = inMemoryStore.subjects || [];
    return subjectIds
        .map(id => {
            const subject = getById(subjects, id);
            if (!subject) return null;
            return {
                id: subject.id,
                subject_code: subject.subject_code,
                name: subject.name,
                color: subject.color
            };
        })
        .filter(s => s !== null); // Filter out any missing subjects
};

// Entry point used by apiService
export const mockFetch = async (endpointPath, method = 'GET', body = null, queryParams = null /* options unused */) => {
    await ensureInitialized();

    const m = method.toUpperCase();
    const parts = parsePath(endpointPath);

    // Teachers
    if (parts[0] === 'teachers') {
        if (m === 'GET' && parts.length === 1) {
            // Resolve qualifiedSubjects for all teachers
            const teachers = (inMemoryStore.teachers || []).map(teacher => ({
                ...teacher,
                qualifiedSubjects: resolveQualifiedSubjects(teacher.qualifiedSubjects)
            }));
            return jsonResponse(teachers);
        }
        if (m === 'POST' && parts.length === 1) {
            const { teacher_code, name, email, tags } = body || {};
            const item = {
                id: nextId('teachers'),
                teacher_code,
                name,
                email,
                tags: Array.isArray(tags) ? tags : [],
                preferences: [],
                qualifiedSubjects: []
            };
            inMemoryStore.teachers.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const teacherId = parts[1];
            const teacher = getById(inMemoryStore.teachers, teacherId);
            if (!teacher) throw new Error('Teacher not found');
            if (m === 'GET' && parts.length === 2) {
                // Resolve qualifiedSubjects from IDs to full objects
                const resolvedTeacher = {
                    ...teacher,
                    qualifiedSubjects: resolveQualifiedSubjects(teacher.qualifiedSubjects)
                };
                return jsonResponse(resolvedTeacher);
            }
            if (m === 'PUT' && parts.length === 2) {
                Object.assign(teacher, body || {});
                if (body && body.hasOwnProperty('tags')) {
                    teacher.tags = Array.isArray(body.tags) ? body.tags : [];
                }
                persist();
                // Resolve qualifiedSubjects from IDs to full objects when returning
                const resolvedTeacher = {
                    ...teacher,
                    qualifiedSubjects: resolveQualifiedSubjects(teacher.qualifiedSubjects)
                };
                return jsonResponse(resolvedTeacher);
            }
            if (m === 'DELETE' && parts.length === 2) {
                removeById(inMemoryStore.teachers, teacherId);
                persist();
                return null;
            }
            if (parts[2] === 'preferences' && m === 'PUT') {
                const formatted = (Array.isArray(body) ? body : []).map(p => p.startTime ? mapPreferenceToTeacherApiFormat(p) : p);
                teacher.preferences = formatted;
                persist();
                return jsonResponse(formatted);
            }
            if (parts[2] === 'qualifications' && m === 'PUT') {
                // Extract IDs if full objects are passed, otherwise use IDs directly
                const subjectIds = Array.isArray(body) 
                    ? body.map(item => typeof item === 'object' && item !== null ? item.id : item)
                    : [];
                teacher.qualifiedSubjects = subjectIds;
                persist();
                // Return resolved full objects (matching backend behavior)
                return jsonResponse(resolveQualifiedSubjects(teacher.qualifiedSubjects));
            }
        }
    }

    // Groups
    if (parts[0] === 'groups') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.groups || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const { group_code, name } = body || {};
            const item = { id: nextId('groups'), group_code, name, students: [], preferences: [], tags: [] };
            inMemoryStore.groups.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const groupId = parts[1];
            const group = getById(inMemoryStore.groups, groupId);
            if (!group) throw new Error('Group not found');
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(group);
            }
            if (m === 'PUT' && parts.length === 2) {
                Object.assign(group, body || {});
                persist();
                return jsonResponse(group);
            }
            if (m === 'DELETE' && parts.length === 2) {
                removeById(inMemoryStore.groups, groupId);
                persist();
                return null;
            }
            if (parts[2] === 'students') {
                if (m === 'POST' && parts.length === 3) {
                    const studentId = body?.studentId;
                    const student = getById(inMemoryStore.students, studentId);
                    if (!student) throw new Error('Student not found');
                    if (!group.students.find(s => s.id === student.id)) {
                        group.students.push({ id: student.id, first_name: student.first_name, family_name: student.family_name, email: student.email });
                    }
                    persist();
                    return jsonResponse({ ok: true });
                }
                if (m === 'POST' && parts.length === 4 && parts[3] === 'batch') {
                    const students = Array.isArray(body?.students) ? body.students : [];
                    let successful = 0; let failed = 0;
                    students.forEach(s => {
                        try {
                            let existing = inMemoryStore.students.find(st => st.email === s.email);
                            if (!existing) {
                                existing = { id: nextId('students'), ...s };
                                inMemoryStore.students.push(existing);
                            }
                            if (!group.students.find(gs => gs.id === existing.id)) {
                                group.students.push({ id: existing.id, first_name: existing.first_name, family_name: existing.family_name, email: existing.email });
                            }
                            successful += 1;
                        } catch (e) { failed += 1; }
                    });
                    persist();
                    return jsonResponse({ results: { successful, failed } });
                }
                if (m === 'DELETE' && parts.length === 4) {
                    const studentId = parts[3];
                    const idx = group.students.findIndex(s => String(s.id) === String(studentId));
                    if (idx >= 0) group.students.splice(idx, 1);
                    persist();
                    return null;
                }
            }
            if (parts[2] === 'preferences' && m === 'PUT') {
                const formatted = (Array.isArray(body) ? body : []).map(mapPreferenceToGroupApiFormat);
                group.preferences = formatted;
                persist();
                return jsonResponse(formatted);
            }
        }
    }

    // Students
    if (parts[0] === 'students') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.students || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const { email } = body || {};
            let existing = inMemoryStore.students.find(s => s.email === email);
            if (existing) return jsonResponse(existing);
            const item = { id: nextId('students'), wants_emails: true, ...body };
            inMemoryStore.students.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const studentId = parts[1];
            const student = getById(inMemoryStore.students, studentId);
            if (!student) throw new Error('Student not found');
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(student);
            }
            if (parts[2] === 'email-preference' && m === 'PATCH') {
                student.wants_emails = !!body?.wants_emails;
                persist();
                return jsonResponse({ ok: true });
            }
            if (parts[2] === 'unsubscribe' && m === 'POST') {
                student.wants_emails = false;
                persist();
                return jsonResponse({ ok: true });
            }
        }
    }

    // Rooms
    if (parts[0] === 'rooms') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.rooms || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const item = { id: nextId('rooms'), preferences: [], ...body };
            inMemoryStore.rooms.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts.length >= 2) {
            const roomId = parts[1];
            const room = getById(inMemoryStore.rooms, roomId);
            if (!room) throw new Error('Room not found');
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(room);
            }
            if (m === 'PUT' && parts.length === 2) {
                Object.assign(room, body || {});
                persist();
                return jsonResponse(room);
            }
            if (m === 'DELETE' && parts.length === 2) {
                removeById(inMemoryStore.rooms, roomId);
                persist();
                return null;
            }
            if (parts[2] === 'preferences' && m === 'PUT') {
                room.preferences = Array.isArray(body) ? body : [];
                persist();
                return jsonResponse(room.preferences);
            }
        }
    }

    // Subjects
    if (parts[0] === 'subjects') {
        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.subjects || []);
        }
        if (m === 'POST' && parts.length === 1) {
            const item = { id: nextId('subjects'), ...body };
            inMemoryStore.subjects.push(item);
            persist();
            return jsonResponse(item);
        }
        if (m === 'DELETE' && parts.length === 2 && parts[1] === 'delete-all') {
            inMemoryStore.subjects = [];
            persist();
            return jsonResponse({ ok: true });
        }
        if (parts.length >= 2) {
            const id = parts[1];
            const subj = getById(inMemoryStore.subjects, id);
            if (!subj) throw new Error('Subject not found');
            if (m === 'GET' && parts.length === 2) return jsonResponse(subj);
            if (m === 'PUT' && parts.length === 2) { Object.assign(subj, body || {}); persist(); return jsonResponse(subj); }
            if (m === 'DELETE' && parts.length === 2) { removeById(inMemoryStore.subjects, id); persist(); return null; }
        }
    }

    // Schedule Entries
    if (parts[0] === 'schedule-entries') {
        if (m === 'GET' && parts.length === 1) {
            // Optionally filter by queryParams
            let list = inMemoryStore.schedule_entries || [];
            const activePeriodId = queryParams?.active_period_id || queryParams?.activePeriodId;
            if (activePeriodId) {
                list = filterEntriesForActivePeriod(inMemoryStore, activePeriodId);
            }
            if (queryParams && Object.keys(queryParams).length) {
                list = list.filter(e => Object.entries(queryParams).every(([k, v]) => {
                    if (k === 'active_period_id' || k === 'activePeriodId') return true;
                    if (k === 'discard' && String(v) === 'false') return e.discard === false || e.discard == null;
                    if (k === 'draft_name') {
                        if (v === 'live' || v === '' || v == null) {
                            return e.draft_name == null || e.draft_name === '';
                        }
                        return entryMatchesDraftName(inMemoryStore, e, String(v));
                    }
                    return String(e[k]) === String(v);
                }));
            }
            return jsonResponse(list);
        }
        if (m === 'POST' && parts.length === 1) {
            const item = { id: nextId('schedule_entries'), ...body };
            inMemoryStore.schedule_entries.push(item);
            persist();
            return jsonResponse(item);
        }
        if (parts[1] === 'import' && m === 'POST') {
            const arr = Array.isArray(body) ? body : [];
            arr.forEach(e => { inMemoryStore.schedule_entries.push({ id: nextId('schedule_entries'), ...e }); });
            persist();
            return jsonResponse({ count: arr.length });
        }
        if (parts[1] === 'drafts') {
            if (m === 'GET' && parts.length === 2) {
                return jsonResponse(buildDraftListFromStore(inMemoryStore));
            }
            if (m === 'POST' && parts.length === 3 && parts[2] === 'create_empty') {
                const { draft_name, year, period_id, display_name } = body || {};
                if (!draft_name) throw new Error('Draft name is required');
                if (findScheduleDraftByName(inMemoryStore, draft_name)) {
                    throw new Error(`A draft named "${draft_name}" already exists.`);
                }
                const draftId = nextId('schedule_drafts');
                const resolvedDisplayName = (typeof display_name === 'string' && display_name.trim())
                    ? display_name.trim()
                    : (cleanInheritedTitle(draft_name) || draft_name);
                const draft = {
                    id: draftId,
                    draft_name,
                    name: draft_name,
                    display_name: resolvedDisplayName,
                    version_number: nextVersionNumberInStore(inMemoryStore, period_id || null),
                    year: year || null,
                    period_id: period_id || null,
                    institution_id: 1,
                    kind: 'version',
                    updated_at: new Date().toISOString()
                };
                ensureScheduleDrafts(inMemoryStore).push(draft);
                inMemoryStore.schedule_entries.push({
                    id: nextId('schedule_entries'),
                    entry_type: 'other',
                    draft_name,
                    schedule_draft_id: draftId,
                    year: year || new Date().getFullYear(),
                    period_id: period_id || null,
                    day: 'monday',
                    start_time: '00:00:00',
                    end_time: '00:01:00',
                    week_series: '999',
                    subject_name: '__PLACEHOLDER__',
                    teacher_name: '__PLACEHOLDER__',
                    room_name: '__PLACEHOLDER__',
                    discard: false
                });
                persist();
                return jsonResponse({
                    message: `Empty draft "${draft_name}" created successfully.`,
                    draft_name,
                    display_name: draft.display_name,
                    version_number: draft.version_number,
                    year: draft.year,
                    period_id: draft.period_id
                });
            }
            if (parts.length >= 3) {
                const draftName = decodeURIComponent(parts[2]);
                const draftMeta = findScheduleDraftByName(inMemoryStore, draftName);
                const legacyDraft = (inMemoryStore.drafts || []).find((d) => d.name === draftName || d.draft_name === draftName);

                if (m === 'POST' && parts.length === 4 && parts[3] === 'save') {
                    const { year, period_id, overwrite } = body || {};
                    let draftId = draftMeta?.id;
                    const scheduleDrafts = ensureScheduleDrafts(inMemoryStore);
                    if (!draftId) {
                        draftId = nextId('schedule_drafts');
                        scheduleDrafts.push({
                            id: draftId,
                            draft_name: draftName,
                            name: draftName,
                            year: year || null,
                            period_id: period_id || null,
                            institution_id: 1,
                            updated_at: new Date().toISOString()
                        });
                    } else if (draftMeta) {
                        draftMeta.year = year ?? draftMeta.year;
                        draftMeta.period_id = period_id ?? draftMeta.period_id;
                        draftMeta.updated_at = new Date().toISOString();
                    }

                    const sourceEntries = (inMemoryStore.schedule_entries || []).filter((e) => (
                        period_id
                            ? String(e.period_id || e.periodId) === String(period_id)
                            : String(e.year) === String(year)
                    ) && (e.discard === false || e.discard == null));

                    if (overwrite || draftId) {
                        inMemoryStore.schedule_entries = (inMemoryStore.schedule_entries || []).filter(
                            (e) => String(e.schedule_draft_id ?? e.scheduleDraftId) !== String(draftId)
                        );
                    }

                    const clones = sourceEntries
                        .filter((e) => String(e.schedule_draft_id ?? e.scheduleDraftId) !== String(draftId))
                        .map((e) => ({
                            ...e,
                            id: nextId('schedule_entries'),
                            draft_name: draftName,
                            schedule_draft_id: draftId,
                            period_id: period_id ?? e.period_id ?? e.periodId ?? null
                        }));
                    inMemoryStore.schedule_entries.push(...clones);

                    if (legacyDraft) {
                        legacyDraft.entries = clones;
                        legacyDraft.year = year;
                        legacyDraft.period_id = period_id || legacyDraft.period_id || null;
                    }
                    persist();
                    return jsonResponse({ ok: true });
                }
                if (m === 'POST' && parts.length === 4 && parts[3] === 'load') {
                    const entries = draftMeta
                        ? (inMemoryStore.schedule_entries || []).filter((e) => entryMatchesDraftName(inMemoryStore, e, draftName))
                        : (legacyDraft?.entries || []);
                    if (!entries.length && !draftMeta && !legacyDraft) throw new Error('Draft not found');
                    const yearFilter = legacyDraft?.year ?? draftMeta?.year;
                    inMemoryStore.schedule_entries = (inMemoryStore.schedule_entries || []).filter(
                        (e) => !yearFilter || String(e.year) !== String(yearFilter)
                    ).concat(entries);
                    persist();
                    return jsonResponse({ ok: true });
                }
                if (m === 'DELETE' && parts.length === 3) {
                    const draftId = draftMeta?.id;
                    if (draftId != null) {
                        inMemoryStore.schedule_entries = (inMemoryStore.schedule_entries || []).filter(
                            (e) => String(e.schedule_draft_id ?? e.scheduleDraftId) !== String(draftId)
                        );
                        removeById(ensureScheduleDrafts(inMemoryStore), draftId);
                    }
                    if (legacyDraft) removeById(inMemoryStore.drafts, legacyDraft.id);
                    if (!draftMeta && !legacyDraft) throw new Error('Draft not found');
                    persist();
                    return null;
                }
                if (m === 'POST' && parts.length === 5 && parts[3] === 'publish-period') {
                    // No-op publish in mock
                    return jsonResponse({ ok: true });
                }
                if (m === 'POST' && parts.length === 4 && parts[3] === 'duplicate') {
                    const sourceName = draftName;
                    const targetName = String(body?.newName || '').trim();
                    if (!targetName) throw new Error('New draft name is required');
                    if (sourceName === targetName) throw new Error('New draft name must be different from source draft name');

                    const existingTarget = findScheduleDraftByName(inMemoryStore, targetName);
                    if (existingTarget) {
                        throw new Error(`Draft with name "${targetName}" already exists`);
                    }

                    const sourceEntries = draftMeta
                        ? (inMemoryStore.schedule_entries || []).filter((e) => entryMatchesDraftName(inMemoryStore, e, sourceName))
                        : (legacyDraft?.entries || []);

                    if (!draftMeta && !legacyDraft && sourceEntries.length === 0) {
                        throw new Error(`Draft "${sourceName}" not found`);
                    }

                    const periodId = draftMeta?.period_id ?? draftMeta?.periodId
                        ?? legacyDraft?.period_id ?? legacyDraft?.periodId
                        ?? null;
                    const year = draftMeta?.year ?? legacyDraft?.year ?? sourceEntries[0]?.year ?? null;
                    const copiedDisplayName = draftMeta?.display_name
                        || legacyDraft?.display_name
                        || cleanInheritedTitle(sourceName)
                        || sourceName;
                    const copiedVersionNumber = nextVersionNumberInStore(inMemoryStore, periodId);
                    const targetDraftId = nextId('schedule_drafts');
                    const targetDraft = {
                        id: targetDraftId,
                        draft_name: targetName,
                        name: targetName,
                        display_name: copiedDisplayName,
                        version_number: copiedVersionNumber,
                        year,
                        period_id: periodId,
                        institution_id: 1,
                        kind: 'version',
                        source_draft_id: draftMeta?.id ?? null,
                        updated_at: new Date().toISOString()
                    };
                    ensureScheduleDrafts(inMemoryStore).push(targetDraft);

                    const clones = sourceEntries.map((e) => {
                        const { id: _omitId, ...rest } = e;
                        return {
                            ...rest,
                            id: nextId('schedule_entries'),
                            draft_name: targetName,
                            schedule_draft_id: targetDraftId,
                            period_id: periodId ?? e.period_id ?? e.periodId ?? null
                        };
                    });
                    inMemoryStore.schedule_entries.push(...clones);
                    persist();

                    return jsonResponse({
                        message: `Draft "${sourceName}" duplicated as "${targetName}"`,
                        draft_name: targetName,
                        display_name: copiedDisplayName,
                        version_number: copiedVersionNumber,
                        source_draft_name: sourceName,
                        period_id: periodId,
                        entries_count: clones.length
                    });
                }
                if (m === 'PUT' && parts.length === 4 && parts[3] === 'rename') {
                    const newName = String(body?.newName || '').trim();
                    if (!newName) throw new Error('New draft name is required');
                    if (!draftMeta && !legacyDraft) throw new Error('Draft not found');
                    if (draftMeta) {
                        draftMeta.display_name = newName;
                        draftMeta.updated_at = new Date().toISOString();
                    }
                    if (legacyDraft) {
                        legacyDraft.display_name = newName;
                    }
                    persist();
                    return jsonResponse({
                        ok: true,
                        draft_name: draftName,
                        display_name: newName
                    });
                }
            }
        }
        if (parts.length === 2) {
            const id = parts[1];
            const entry = getById(inMemoryStore.schedule_entries, id);
            if (!entry) throw new Error('Schedule entry not found');
            if (m === 'PUT') { Object.assign(entry, body || {}); persist(); return jsonResponse(entry); }
            if (m === 'DELETE') { removeById(inMemoryStore.schedule_entries, id); persist(); return null; }
        }
        if (parts[1] === 'debug' && parts[2] === 'delete-all' && m === 'DELETE') {
            inMemoryStore.schedule_entries = [];
            persist();
            return null;
        }
        if (parts[1] === 'notify' && m === 'POST') {
            // Return a trivial result indicating notifications would be sent
            return jsonResponse({ ok: true, messagesSent: (Array.isArray(body?.changes) ? body.changes.length : 0) });
        }
    }

    // Year Structure
    if (parts[0] === 'year-structure') {
        const ensureYearStructure = () => {
            if (!inMemoryStore.yearStructure) {
                inMemoryStore.yearStructure = {
                    id: 1,
                    academicYearId: 1,
                    academicYear: '2025-2026',
                    startWeek: 1,
                    endWeek: 53,
                    periods: [],
                    holidays: []
                };
            }
            return inMemoryStore.yearStructure;
        };

        if (m === 'POST' && parts.length === 2 && parts[1] === 'periods') {
            const ys = ensureYearStructure();
            const periods = ys.periods || [];
            const newId = periods.length ? Math.max(...periods.map((p) => Number(p.id) || 0), 0) + 1 : 1;
            const associateDraftName = body?.associateDraftName ?? body?.associate_draft_name;
            const period = { ...(body || {}), id: newId };
            delete period.associateDraftName;
            delete period.associate_draft_name;
            ys.periods = [...periods, period];

            if (associateDraftName) {
                const draft = findScheduleDraftByName(inMemoryStore, associateDraftName);
                if (draft) {
                    draft.period_id = newId;
                    draft.periodId = newId;
                    if (period.year != null) draft.year = period.year;
                }

                inMemoryStore.schedule_entries = (inMemoryStore.schedule_entries || []).map((entry) => {
                    const matches = draft
                        ? entryMatchesDraftName(inMemoryStore, entry, associateDraftName)
                        : entry.draft_name === associateDraftName;
                    return matches ? { ...entry, period_id: newId, periodId: newId } : entry;
                });

                const draftId = draft?.id;
                if (draftId != null) {
                    const livePeriods = (ys.periods || []).filter((existing) => {
                        const activeId = existing.active_schedule_draft_id ?? existing.activeScheduleDraftId;
                        return String(activeId) === String(draftId) && String(existing.id) !== String(newId);
                    });
                    if (livePeriods.length > 0) {
                        ys.periods = ys.periods.map((existing) => {
                            const activeId = existing.active_schedule_draft_id ?? existing.activeScheduleDraftId;
                            if (String(activeId) === String(draftId) && String(existing.id) !== String(newId)) {
                                return { ...existing, active_schedule_draft_id: null, activeScheduleDraftId: null };
                            }
                            return existing;
                        });
                        const settings = inMemoryStore.institutionSettings || {};
                        const current = settings.live_schedule_version != null ? settings.live_schedule_version : 1;
                        inMemoryStore.institutionSettings = { ...settings, live_schedule_version: current + 1 };
                    }
                }

                const legacyDraft = (inMemoryStore.drafts || []).find(
                    (d) => d.draft_name === associateDraftName || d.name === associateDraftName
                );
                if (legacyDraft) {
                    legacyDraft.period_id = newId;
                    legacyDraft.periodId = newId;
                }
            }

            persist();
            return jsonResponse({ message: 'OK', yearStructure: ys, period });
        }
        if (m === 'PATCH' && parts.length === 3 && parts[1] === 'periods') {
            const ys = ensureYearStructure();
            const pid = parts[2];
            const periods = ys.periods || [];
            const idx = periods.findIndex((p) => String(p.id) === String(pid));
            if (idx < 0) throw new Error('Period not found');
            const previousPeriod = periods[idx];
            const previousActiveDraftId = previousPeriod.active_schedule_draft_id ?? previousPeriod.activeScheduleDraftId ?? null;
            const nextActiveDraftId = body?.active_schedule_draft_id ?? body?.activeScheduleDraftId ?? previousActiveDraftId;
            if (String(previousActiveDraftId ?? '') !== String(nextActiveDraftId ?? '')) {
                archiveOutgoingLiveDraftInStore(inMemoryStore, previousPeriod, nextActiveDraftId);
            }
            periods[idx] = { ...previousPeriod, ...(body || {}) };
            ys.periods = periods;
            if (String(previousActiveDraftId ?? '') !== String(nextActiveDraftId ?? '')) {
                const settings = inMemoryStore.institutionSettings || {};
                const current = settings.live_schedule_version != null ? settings.live_schedule_version : 1;
                inMemoryStore.institutionSettings = { ...settings, live_schedule_version: current + 1 };
            }
            persist();
            return jsonResponse({ message: 'OK', yearStructure: ys });
        }
        if (m === 'DELETE' && parts.length === 3 && parts[1] === 'periods') {
            const ys = ensureYearStructure();
            const pid = parts[2];
            ys.periods = (ys.periods || []).filter((p) => String(p.id) !== String(pid));
            persist();
            return jsonResponse({ message: 'OK', yearStructure: ys });
        }

        if (m === 'GET' && parts.length === 1) {
            return jsonResponse(inMemoryStore.yearStructure || null);
        }
        if (m === 'POST' && parts.length === 1) {
            // Bulk holiday replace only (periods use /year-structure/periods in real API)
            const existing = inMemoryStore.yearStructure || {};
            const existingPeriods = existing.periods || [];
            const existingHolidays = existing.holidays || [];

            const holidays = (body.holidays || []).map((holiday, idx) => {
                if (holiday.id) {
                    const existingHoliday = existingHolidays.find(h => h.id === holiday.id);
                    if (existingHoliday) {
                        return { ...existingHoliday, ...holiday, id: existingHoliday.id };
                    }
                }
                const matched = existingHolidays.find(h =>
                    h.name === holiday.name &&
                    h.startDate === holiday.startDate &&
                    h.endDate === holiday.endDate
                );
                return {
                    ...holiday,
                    id: matched ? matched.id : (existingHolidays.length > 0 ? Math.max(...existingHolidays.map(h => h.id || 0)) + idx + 1 : idx + 1)
                };
            });

            inMemoryStore.yearStructure = {
                ...existing,
                ...body,
                periods: existingPeriods,
                holidays
            };
            persist();

            return jsonResponse({
                message: 'Year structure saved successfully',
                yearStructure: inMemoryStore.yearStructure
            });
        }
        if (m === 'GET' && parts.length === 2 && parts[1] === 'years') {
            // Minimal summary: one item if yearStructure exists
            const ys = inMemoryStore.yearStructure;
            const years = ys ? [{ id: ys.academicYearId || 1, name: ys.academicYear, startWeek: ys.startWeek, endWeek: ys.endWeek }] : [];
            return jsonResponse(years);
        }
        if (m === 'DELETE' && parts.length === 3 && parts[1] === 'years') {
            inMemoryStore.yearStructure = null;
            persist();
            return null;
        }
    }

    // Institution
    if (parts[0] === 'institution') {
        if (parts[1] === 'live-schedule-version' && m === 'GET' && parts.length === 2) {
            const settings = inMemoryStore.institutionSettings || {};
            const live_schedule_version = settings.live_schedule_version != null ? settings.live_schedule_version : 1;
            return jsonResponse({ live_schedule_version });
        }
        if (parts[1] === 'settings') {
            if (m === 'GET') return jsonResponse(inMemoryStore.institutionSettings || {});
            if (m === 'PUT') { inMemoryStore.institutionSettings = { ...(inMemoryStore.institutionSettings || {}), ...body }; persist(); return jsonResponse(inMemoryStore.institutionSettings); }
            // POST /institution/settings/icon — mock URL (no real file in NO_SERVER mode)
            if (m === 'POST' && parts[2] === 'icon') {
                return jsonResponse({ url: 'https://via.placeholder.com/128.png?text=School+icon' });
            }
        }
        if (parts[1] === 'admins') {
            if (m === 'GET' && parts.length === 2) return jsonResponse(inMemoryStore.admins || []);
            if (parts[2] === 'invitations') {
                // POST /institution/admins/invitations
                if (m === 'POST' && parts.length === 3) {
                    const inv = { id: nextId('invitations'), email: body?.email, token: `token_${Date.now()}`, status: 'pending' };
                    inMemoryStore.invitations.push(inv);
                    persist();
                    return jsonResponse(inv);
                }
                // POST /institution/admins/invitations/:invitationId/resend
                if (m === 'POST' && parts.length === 5 && parts[4] === 'resend') {
                    return jsonResponse({ ok: true });
                }
                // DELETE /institution/admins/invitations/:invitationId
                if (m === 'DELETE' && parts.length === 4) {
                    const id = parts[3];
                    removeById(inMemoryStore.invitations, id);
                    persist();
                    return null;
                }
            }
            // DELETE /institution/admins/:userId
            if (m === 'DELETE' && parts.length === 3) {
                const id = parts[2];
                removeById(inMemoryStore.admins, id);
                persist();
                return null;
            }
        }
    }

    // Setup onboarding
    if (parts[0] === 'setup' && parts[1] === 'institution' && m === 'POST') {
        const { institutionName, name } = body || {};
        inMemoryStore.institutionSettings = { ...(inMemoryStore.institutionSettings || {}), name: institutionName };
        inMemoryStore.admins = inMemoryStore.admins || [];
        const newAdmin = { id: nextId('admins'), email: `${name?.toLowerCase() || 'admin'}@example.com`, role: 'Admin' };
        inMemoryStore.admins.push(newAdmin);
        persist();
        return jsonResponse({ ok: true, admin: newAdmin });
    }

    // Invitations public endpoints
    if (parts[0] === 'invitations') {
        if (parts[1] === 'accept' && m === 'POST') {
            const inv = inMemoryStore.invitations.find(i => i.token === body?.token);
            if (!inv) throw new Error('Invalid token');
            inv.status = 'accepted';
            persist();
            return jsonResponse({ ok: true });
        }
        if (parts[1] === 'lookup' && m === 'GET') {
            const token = queryParams?.token;
            const inv = inMemoryStore.invitations.find(i => i.token === token);
            if (!inv) throw new Error('Invitation not found');
            return jsonResponse({ email: inv.email, status: inv.status });
        }
    }

    logger.warn('[mockApiService] Unhandled route:', method, endpointPath);
    return jsonResponse({});
};

export /**
 * Reset mock data cache to force reload from mock-data.json on next access.
 * This is useful when you've edited mock-data.json and want to see the changes.
 */
const resetMockData = () => {
    inMemoryStore = null;
    idTracker = null;
    try { 
        sessionStorage.removeItem(STORAGE_KEY); 
        sessionStorage.removeItem(ID_TRACKER_KEY); 
    } catch (e) {
        // ignore
    }
};

export const initializeApiService = () => {};

export default {
    mockFetch,
    resetMockData,
    initializeApiService
};


